import { useContext } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import {
  ErrorDialogContext,
  errorHandledAsync,
} from "@/shared/context/error-context";
import { DownloadTradesService } from "@/features/accounts/shared/services/download-trades-service";
import { DownloadTradesPostRequestModel } from "@/features/accounts/shared/services/download-trades-model";

interface Props {
  onTapCompleteModal: () => void;
}

export interface SearchFilters {
  tradeType?: 0 | 1 | 2;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  minBalance?: number;
  maxBalance?: number;
  summaryKeyword?: string;
  memoKeyword?: string;
}

const STATUS_QUEUED = 0;
const STATUS_RUNNING = 1;
const STATUS_SUCCEEDED = 2;
const STATUS_FAILED = 3;
const POLLING_INTERVAL_MS = 5000;
const POLLING_MAX_COUNT = 120; // 5秒 * 120 = 最大10分待機する。

export const useDownloadTrades = ({ onTapCompleteModal }: Props) => {
  const downloadTradesService = new DownloadTradesService();
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const downloadFile = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename; // ファイル名指定可
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildBaseRequest = (
    accountGroupId: string,
    fileName: string,
    passbookInfo: string,
  ): DownloadTradesPostRequestModel => ({
    account_group_id: accountGroupId,
    file_name: fileName,
    passbook_info: passbookInfo,
  });

  const applySearchFilters = (
    param: DownloadTradesPostRequestModel,
    searchFilters?: SearchFilters,
  ) => {
    if (!searchFilters) return;

    if (searchFilters.tradeType !== undefined && searchFilters.tradeType !== 0) {
      param.trade_type = searchFilters.tradeType;
    }
    if (searchFilters.startDate) {
      param.start_date = searchFilters.startDate;
    }
    if (searchFilters.endDate) {
      param.end_date = searchFilters.endDate;
    }
    if (searchFilters.minAmount !== undefined) {
      param.amount_low = searchFilters.minAmount;
    }
    if (searchFilters.maxAmount !== undefined) {
      param.amount_high = searchFilters.maxAmount;
    }
    if (searchFilters.minBalance !== undefined) {
      param.balance_low = searchFilters.minBalance;
    }
    if (searchFilters.maxBalance !== undefined) {
      param.balance_high = searchFilters.maxBalance;
    }
    if (searchFilters.summaryKeyword) {
      param.transact_match = searchFilters.summaryKeyword;
    }
    if (searchFilters.memoKeyword) {
      param.memo_match = searchFilters.memoKeyword;
    }
  };

  const pollUntilFinished = async (downloadJobId: string) => {
    for (let index = 0; index < POLLING_MAX_COUNT; index += 1) {
      const statusResult = await downloadTradesService.getDownloadExcelJobStatus(downloadJobId);
      const status = statusResult.status;

      if (status === STATUS_SUCCEEDED) {
        return;
      }
      if (status === STATUS_FAILED) {
        throw new Error("Excelファイル作成に失敗しました。\nしばらくお待ちいただいた後に再度お試しください。");
      }
      if (status !== STATUS_QUEUED && status !== STATUS_RUNNING) {
        throw new Error("Excelファイル作成状態が不正です。");
      }

      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
    }

    throw new Error("Excelファイル作成が完了しませんでした。時間をおいて再実行してください。");
  };

  const downloadAsyncExcel = async (param: DownloadTradesPostRequestModel, fileName: string) => {
    const startResult = await downloadTradesService.startDownloadExcelJob(param);
    if (startResult.message && !startResult.download_job_id) {
      throw new Error(startResult.message);
    }

    const downloadJobId = startResult.download_job_id;
    if (!downloadJobId) {
      throw new Error("Excelファイル作成ジョブの開始に失敗しました。");
    }

    // グループ単位だけは、Job完了まで画面側でポーリングしてから成果物を取得する。
    await pollUntilFinished(downloadJobId);
    const blob = await downloadTradesService.downloadExcelJobFile(downloadJobId);
    const url = window.URL.createObjectURL(blob);
    downloadFile(url, fileName);
  };

  const onClickDownload = async (
    accountGroupId: string,
    fileName: string,
    passbookInfo: string,
    searchFilters?: SearchFilters,
    isGroupDownload = false,
  ) => {
    loadingCtx.setIsLoading(true);
    try {
      const param = buildBaseRequest(accountGroupId, fileName, passbookInfo);
      if (isGroupDownload) {
        await downloadAsyncExcel(param, fileName);
      } else {
        // 個別口座は既存同期APIを維持するため、検索条件も従来通り付与する。
        applySearchFilters(param, searchFilters);
        const res = await downloadTradesService.downloadTrades(param);
        const url = window.URL.createObjectURL(res);
        downloadFile(url, fileName);
      }
      onTapCompleteModal();
    } catch (error) {
      console.log("onClickDownload:", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  return {
    onClickDownload: errorHandledAsync(onClickDownload, setError),
  };
};
